import React, { useEffect, useState } from 'react';
import ClayCard from "@clayui/card";
import ClayLayout from "@clayui/layout";
import ClayButton from "@clayui/button";
import './CategoryServicesPage.css';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import Pagination from '../../generic-components/Pagination/Pagination';
import Loader from './custom-loader/Loader';
import NoDataFound from '../../generic-components/NoDataFound/NoDataFound';
import { useApiMutation } from '../../apis/useApi';
import { LANGUAGE } from '../../utils/languageProperties';

interface ServiceItem {
    id: string;
    title: string;
}

interface OutletContextType {
    selectedCategoryName: string;
    searchValue: string;
    setSearchValue: (value: string) => void;
}

    const scopeKey = 'BID Partner';

const CategoryServicesPage = () => {
    const { categoryId } = useParams<{ categoryId: string }>();
    const { selectedCategoryName, searchValue, setSearchValue } =
        useOutletContext<OutletContextType>();
    const selectedCategory = JSON.parse(
        sessionStorage.getItem('selectedCategory') || 'null'
    );
    const currentLocale =
        (window as any)?.Liferay?.ThemeDisplay?.getBCP47LanguageId?.() ||
        'en-US';

    const [subCategoryData, setSubCategoryData] = useState<ServiceItem[]>([]);
    const [filteredData, setFilteredData] = useState<ServiceItem[]>([]);
    const [partnerSelectedCategory, setPartnerSelectedCategory] =
        useState<any>();
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);

    const itemsPerPage = 6;
    const navigate = useNavigate();

    const getPartnerOffers = useApiMutation<any, {}>(
        `o/c/partneroffers/scopes/${scopeKey}`,
        'get'
    );

    useEffect(() => {
        const fetchPartnerOffers = async () => {
            try {
                const result = await getPartnerOffers.mutateAsync({
                    data: {},
                    params: {
                        p_auth: (window as any)?.Liferay?.authToken,
                        filter: `partnersSelectedCategory eq '${
                            selectedCategory?.englishName ||
                            selectedCategoryName ||
                            selectedCategory?.name
                        }' and (offerStatus ne 'suspendedServiceOffer' and offerStatus ne 'deactivatedServiceOffer' and offerStatus ne 'pendingOfferApprovalFromPartner' and offerStatus ne 'pendingOfferApprovalFromAccountManager' and offerStatus ne 'accountManagerNeedRevision' and offerStatus ne 'partnerNeedRevision')`,
                        pageSize: 0
                    }
                });

                const usedCategoryIds = new Set<number>();

                result?.items?.forEach((item: any) => {
                    item.taxonomyCategoryBriefs?.forEach((cat: any) => {
                        usedCategoryIds.add(cat?.taxonomyCategoryId);
                    });
                });

                setPartnerSelectedCategory(
                    Array.from(usedCategoryIds.values())
                );
            } catch (error) {
                console.error('Failed to fetch partner offers', error);
            }
        };

        fetchPartnerOffers();
    }, [categoryId]);

    useEffect(() => {
        const fetchCategoriesWithParent = async () => {
            if (!partnerSelectedCategory?.length) return;

            setIsLoading(true);

            const ids = partnerSelectedCategory;

            const queryFields = ids
                .map(
                    (id: string, index: number) => `
                cat${index}: taxonomyCategory(taxonomyCategoryId: "${id}") {
                    id
                    name
                    parentTaxonomyCategory {
                        id
                        name
                    }
                }
            `
                )
                .join('\n');

            const query = `
        {
          headlessAdminTaxonomy_v1_0 {
            ${queryFields}
          }
        }`;

            try {
                const res = await fetch(
                    '/o/graphql?p_auth=' + (window as any)?.Liferay?.authToken,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'accept-language': (
                                window as any
                            )?.Liferay?.ThemeDisplay?.getBCP47LanguageId()
                        },
                        body: JSON.stringify({ query })
                    }
                );

                const result = await res.json();

                const data = result?.data?.headlessAdminTaxonomy_v1_0 ?? {};

                const items = Object.values(data);

                const mappedItems: ServiceItem[] = items.map((item: any) => ({
                    id: item?.parentTaxonomyCategory?.id,
                    title: item?.name,
                    parentName: item?.parentTaxonomyCategory?.name || ''
                }));
                const uniqueParentMap = new Map<string, ServiceItem>();

                mappedItems.forEach((item: any) => {
                    if (
                        item.parentName &&
                        !uniqueParentMap.has(item.parentName)
                    ) {
                        uniqueParentMap.set(item.parentName, {
                            id: item?.id,
                            title: item.parentName
                        });
                    }
                });

                const uniqueParentItems = Array.from(uniqueParentMap.values());

                setSubCategoryData(uniqueParentItems);
                setFilteredData(uniqueParentItems);
            } catch (err) {
                console.error('Failed to fetch categories', err);
                setSubCategoryData([]);
                setFilteredData([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCategoriesWithParent();
    }, [partnerSelectedCategory]);

    useEffect(() => {
        if (searchValue) {
            const filteredData = subCategoryData.filter((item) =>
                item.title.toLowerCase().includes(searchValue.toLowerCase())
            );
            setFilteredData(filteredData);
        } else {
            setFilteredData(subCategoryData);
        }
    }, [searchValue]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const currentItems = filteredData.slice(
        indexOfLastItem - itemsPerPage,
        indexOfLastItem
    );

    return (
        <div className="service-parent">
            <div className="service-container">
                <h2 className="block-heading">
                    {selectedCategoryName || selectedCategory?.name}
                </h2>

                {isLoading ? (
                    <Loader />
                ) : (
                    <>
                        {Boolean(currentItems?.length) ? (
                            <ClayLayout.Row className="service-card-container">
                                {currentItems.map((card) => (
                                    <ClayLayout.Col
                                        key={card.id}
                                        size={12}
                                        lg={6}
                                        className="flex justify-center"
                                    >
                                        <ClayCard className="service-card">
                                            <ClayCard.Body>
                                                <div className="service-card-content">
                                                    <h3 className="service-card-title">
                                                        {card.title}
                                                    </h3>

                                                    <ClayButton
                                                        displayType="link"
                                                        className="service-card-btn"
                                                        onClick={() => {
                                                            setSearchValue('');
                                                            sessionStorage.setItem(
                                                                'selectedServiceItem',
                                                                JSON.stringify({
                                                                    ...card,
                                                                    locale: currentLocale
                                                                })
                                                            );
                                                            navigate(
                                                                `/categories/${categoryId}/${card.id}`
                                                            );
                                                        }}
                                                    >
                                                        {LANGUAGE['more']}
                                                    </ClayButton>
                                                </div>
                                            </ClayCard.Body>
                                        </ClayCard>
                                    </ClayLayout.Col>
                                ))}
                            </ClayLayout.Row>
                        ) : (
                            <NoDataFound />
                        )}

                        <Pagination
                            totalItems={filteredData.length}
                            itemsPerPage={itemsPerPage}
                            currentPage={currentPage}
                            onPageChange={setCurrentPage}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default CategoryServicesPage;